import React, { useState, useCallback, useEffect } from "react";
import { UsuarioInterface } from "../interfaces/UsuarioInterface";
import { PlataformasxUsuarioInterface } from "../interfaces/PlataformasxUsuario";
import axios from "axios";

type AuthContextProps = {
  token: string | null;
  usuario: UsuarioInterface | null;
  permisos: string[] | null;
  plataformas: PlataformasxUsuarioInterface[];
  isLoggedIn: boolean;
  login: (
    usuario: UsuarioInterface | null,
    token: string | null,
    permisos: string[],
    plataformas: PlataformasxUsuarioInterface[]
  ) => void;
  tokenCan: (permiso: string) => boolean;
  updatePlatformas: (
    plataformasAgregadas: PlataformasxUsuarioInterface[]
  ) => void;
  logout: () => void;
};

export const AuthContext = React.createContext({} as AuthContextProps);

const retrieveStoredToken = () => {
  const storedToken = localStorage.getItem("token");
  return storedToken;
};

const retrieveStoredUsuario = () => {
  let storedUsuario: UsuarioInterface | null = null;
  if (localStorage.getItem("usuario")) {
    storedUsuario = JSON.parse(localStorage.getItem("usuario") || "{}");
  }

  return storedUsuario;
};

const retireveStoredPermisos = () => {
  let storedPermisos: string[] = [];

  if (localStorage.getItem("permisos")) {
    storedPermisos = JSON.parse(localStorage.getItem("permisos") || "{}");
  }

  return storedPermisos;
};

const retireveStoredPlataformas = () => {
  let storedPlataformas: PlataformasxUsuarioInterface[] = [];

  if (localStorage.getItem("plataformas")) {
    storedPlataformas = JSON.parse(localStorage.getItem("plataformas") || "{}");
  }

  return storedPlataformas;
};

export const AuthContextProvider = ({ children }: any) => {
  const tokenData = retrieveStoredToken();
  const usuarioData = retrieveStoredUsuario();
  const permisosData = retireveStoredPermisos();
  const plataformasData = retireveStoredPlataformas();

  //Lógica de token
  let initialToken: string | null = null;

  if (tokenData) {
    initialToken = tokenData;
  }

  const [token, setToken] = useState<string | null>(initialToken);

  const userIsLoggedIn = !!token;

  //Lógica de usuario
  let initialUsuario: UsuarioInterface | null = null;

  if (usuarioData) {
    initialUsuario = usuarioData;
  }

  const [usuario, setUsuario] = useState<UsuarioInterface | null>(
    initialUsuario
  );

  //Logica de permisos
  let initialPermisos: string[] = [];

  if (permisosData) {
    initialPermisos = permisosData;
  }

  const [permisos, setPermisos] = useState<string[]>(initialPermisos);

  //logica de plataformas
  let initialPlataformas: PlataformasxUsuarioInterface[] = [];

  if (plataformasData) {
    initialPlataformas = plataformasData;
  }

  const [plataformas, setPlataformas] =
    useState<PlataformasxUsuarioInterface[]>(initialPlataformas);

  const logoutHandler = useCallback(() => {
    setToken(null);
    setUsuario(null);
    setPermisos([]);
    setPlataformas([]);
    localStorage.removeItem("token");
    localStorage.removeItem("usuario");
    localStorage.removeItem("permisos");
    localStorage.removeItem("plataformas");
  }, []);

  // Interceptor para incluir plataforma activa en requests
  useEffect(() => {
    console.log('Plataformas en contexto:', plataformas);
    const requestInterceptor = axios.interceptors.request.use((config) => {
      if (plataformas.length > 0 && config.params?.plataforma_activa === undefined) {
        config.params = {
          ...config.params,
          plataforma_activa: plataformas[0].id
        };
      }
      return config;
    });

    // Interceptor de respuesta para manejar token expirado (401)
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          // Token expirado - cerrar sesión
          logoutHandler();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [plataformas, logoutHandler]);

  const loginHandler = (
    usuario: UsuarioInterface | null,
    token: string | null,
    permisos: string[],
    plataformas: PlataformasxUsuarioInterface[]
  ) => {
    setToken(token);
    if (token !== null) {
      localStorage.setItem("token", token);
    }
    setUsuario(usuario);
    if (usuario !== null) {
      localStorage.setItem("usuario", JSON.stringify(usuario));
    }
    setPermisos(permisos);
    if (permisos.length > 0) {
      localStorage.setItem("permisos", JSON.stringify(permisos));
    }
    setPlataformas(plataformas);
    if (plataformas.length > 0) {
      localStorage.setItem("plataformas", JSON.stringify(plataformas));
    }
  };

  const updatePlatformas = (
    plataformasAgregadas: PlataformasxUsuarioInterface[]
  ) => {
    if (plataformasAgregadas.length > 0) {
      setPlataformas(plataformasAgregadas);
      localStorage.setItem("plataformas", JSON.stringify(plataformasAgregadas));
      // Recargar página para aplicar cambios
      window.location.reload();
    }
  };

  const tokenCan = (permiso: string) => {
    return permisos.find((perm) => perm === permiso) ? true : false;
  };

  const contextValue = {
    token: token,
    usuario: usuario,
    permisos: permisos,
    plataformas: plataformas,
    isLoggedIn: userIsLoggedIn,
    login: loginHandler,
    logout: logoutHandler,
    tokenCan,
    updatePlatformas,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
